import React, { useState } from 'react';
import { ArrowRight, PlusIcon } from 'lucide-react';

import { Separator } from '@epoch-ui/components/Separator';
import { Button } from '@epoch-ui/components/Button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '@epoch-ui/components/Accordion';
import { GuestStatus } from 'types/guest.types';
import { MultiSessionPlusOneGuest, PlusOneGuest } from 'types/plus-one.types';
import { QuestionResponse } from 'types/question-response.types';
import { EventType, Session } from 'types/event.types';
import dayjs from 'utils/extendedDayJS';
import { SessionCard, SessionCardTitleAndDate } from 'components/cards/SessionCard';
import { cn } from '@epoch-ui/lib/utils';
import { RsvpSticker } from 'components/cards/components/RsvpSticker/RsvpSticker';

import { useGuestSideSheetContext } from '../guestSideSheetContext';
import { PlusOneGuestModal } from '../Modals/PlusOneGuestModal';
import { formatResponse } from '../utils';

/**
 * @description questionId -> response
 */
type QuestionResponseValueMap = Record<number, QuestionResponse>;

/**
 * @plusOneId -> {questionId: responseValue}
 */
type PlusOneQuestionnaireMap = Record<number, QuestionResponseValueMap>;
const getPlusOneQuestionnaireResponseMaps = (
  plusOnes: PlusOneGuest[] | MultiSessionPlusOneGuest[]
): PlusOneQuestionnaireMap => {
  return plusOnes.reduce(
    (
      acc: PlusOneQuestionnaireMap,
      { plusOneId, questionnaireResponses }: PlusOneGuest | MultiSessionPlusOneGuest
    ): PlusOneQuestionnaireMap => {
      const responseMap = questionnaireResponses.reduce(
        (rmap, response) => ({
          ...rmap,
          [response.id]: response
        }),
        {}
      );
      return { ...acc, [plusOneId]: responseMap };
    },
    {}
  );
};

const PlusOneSessionsSection = ({ plusOneId }: { plusOneId: number }): React.ReactNode => {
  const {
    eventOrSessionId,
    guestData: { event },
    allParentPlusOnes
  } = useGuestSideSheetContext();

  const sessions = event.sessions || [];
  if (event.eventType !== EventType.MULTI_SESSIONS || sessions.length === 0) return null;

  const eventTimezone = event.timezoneCode || 'UTC';
  const displayedTimezone = dayjs.tz.guess() || eventTimezone;

  const plusOneSessionStatuses =
    allParentPlusOnes.find((po) => po.plusOneId === plusOneId)?.sessionStatus || [];

  const sessionStatusMap: { [id: number]: GuestStatus } = {};
  plusOneSessionStatuses.forEach(({ eventId, status }) => {
    sessionStatusMap[eventId] = status;
  });

  const filteredSessions = sessions.filter((session) => session.id in sessionStatusMap);
  if (filteredSessions.length === 0) return null;

  return (
    <div className="tw-flex tw-flex-col tw-gap-1">
      <div className="tw-text-xs tw-font-normal tw-leading-4 tw-text-primary-grey">Sessions</div>
      <div className="tw-flex tw-flex-col tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-border-grey">
        {filteredSessions.map((session: Session) => (
          <SessionCard
            className={cn(
              'tw-rounded-none tw-cursor-default tw-border-0 tw-border-b tw-border-solid tw-border-border-grey last:tw-border-b-0',
              session.id === eventOrSessionId ? '' : 'tw-opacity-40'
            )}
            key={session.id}
            session={session}
            eventTimezone={eventTimezone}
            displayedTimezone={displayedTimezone}
          >
            <SessionCardTitleAndDate />
            <div className="tw-flex tw-items-center tw-justify-between">
              <RsvpSticker
                className="tw-rounded-xl tw-p-2"
                rsvpStatus={sessionStatusMap[session.id]}
              />
            </div>
          </SessionCard>
        ))}
      </div>
    </div>
  );
};

export const GuestSideSheetPlusOnesSection = (): React.ReactNode => {
  const {
    guestData: { guest, event },
    setSelectedPlusOneId,
    selectedSession
  } = useGuestSideSheetContext();
  const plusOneQuestionnaireResponseMaps: PlusOneQuestionnaireMap = React.useMemo(() => {
    if (!guest.plusOnes) return {};
    return getPlusOneQuestionnaireResponseMaps(guest.plusOnes);
  }, [guest.plusOnes]);
  const [isGuestModalOpen, setIsGuestModalOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  const isMultiSessionEvent = event.eventType === EventType.MULTI_SESSIONS;

  if (
    isMultiSessionEvent
      ? !selectedSession?.plusOneConfig?.allowPlusOnes
      : !event.plusOneConfig?.allowPlusOnes
  )
    return null;

  // Only show if owner guest's status is confirmed, incomplete, or waitlisted
  const allowedStatuses = [
    GuestStatus.CONFIRMED,
    GuestStatus.CONFIRMED_WITH_INCOMPLETE_SURVEY,
    GuestStatus.WAITLISTED,
    GuestStatus.ACTION_REQUIRED
  ];
  if (!allowedStatuses.includes(guest.status)) return null;

  const getStatusLabel = (status: GuestStatus): string => {
    switch (status) {
      case GuestStatus.CONFIRMED:
      case GuestStatus.CONFIRMED_WITH_INCOMPLETE_SURVEY:
        return 'Going';
      case GuestStatus.WAITLISTED:
        return 'Waitlisted';
      case GuestStatus.CANCELLED:
        return 'Removed';
      case GuestStatus.ACTION_REQUIRED:
        return 'Pending';
      default:
        return 'Going';
    }
  };

  const handleGuestAdded = (plusOneId: number): void => {
    setExpandedItems([String(plusOneId)]);
  };

  const currentPlusOnesCount = guest.plusOnes?.length ?? 0;
  const maxAllowedPlusOnes = isMultiSessionEvent
    ? selectedSession?.plusOneConfig?.maxPlusOnes ?? 0
    : event.plusOneConfig?.maxPlusOnes ?? 0;

  // For limited MSEs, enforce the session's plus-one capacity using session-scoped count.
  // For non-MSE events, enforce using the guest's total plus-one count.
  // For unlimited MSEs, adding guests is never disabled.
  const sessionPlusOnesCount = isMultiSessionEvent ? guest.plusOnes.length ?? 0 : 0;

  const isAddGuestDisabled = isMultiSessionEvent
    ? event.limitRegistration && sessionPlusOnesCount >= maxAllowedPlusOnes
    : currentPlusOnesCount >= maxAllowedPlusOnes;

  return (
    <>
      <Separator />
      <div className="tw-flex tw-flex-col tw-gap-4">
        <div className="tw-flex tw-flex-row tw-items-center tw-justify-between">
          <span className="tw-text-base tw-font-medium tw-leading-5">
            {guest.firstName}&apos;s guests
          </span>
          <Button
            variant="secondary"
            size="xxs"
            className="tw-rounded-[8px]"
            onClick={() => setIsGuestModalOpen(true)}
            disabled={isAddGuestDisabled}
          >
            <PlusIcon size={14} className="tw-mr-1" />
            New guest
          </Button>
        </div>
        {!guest.plusOnes || guest.plusOnes.length === 0 ? (
          <p className="tw-m-0 tw-text-xs tw-leading-4 tw-text-primary-grey">
            This guest has no plus ones for this event.
          </p>
        ) : (
          <Accordion
            type="multiple"
            value={expandedItems}
            onValueChange={setExpandedItems}
            className="tw-flex tw-flex-col tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-border-grey"
          >
            {(guest.plusOnes as PlusOneGuest[]).map((plusOneGuest, index) => (
              <AccordionItem
                key={plusOneGuest.plusOneId}
                value={String(plusOneGuest.plusOneId)}
                className={`tw-border-0 tw-border-b tw-border-solid tw-border-border-grey tw-px-4 last:tw-border-b-0`}
              >
                <AccordionTrigger className="tw-py-4 hover:tw-no-underline">
                  <div className="tw-flex tw-flex-col tw-items-start tw-gap-1">
                    <span className="tw-text-sm tw-font-medium tw-leading-5">
                      {guest.plusOnes!.length > 1 ? `Guest ${index + 1}: ` : ''}
                      {plusOneGuest.firstName} {plusOneGuest.lastName}
                    </span>

                    <span className="tw-text-sm tw-font-normal tw-leading-5 tw-text-primary-grey">
                      {getStatusLabel(plusOneGuest.status)}
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="tw-flex tw-flex-col tw-gap-4 tw-pb-4">
                  {event.preEventQuestions.map((question) => {
                    const responseMap = plusOneQuestionnaireResponseMaps[plusOneGuest.plusOneId];
                    const questionResponse = responseMap ? responseMap[question.id] : undefined;
                    const response = questionResponse ? formatResponse(questionResponse) : 'N/A';
                    return (
                      <div key={question.id}>
                        <div className="tw-mb-1 tw-text-xs tw-font-normal tw-leading-4 tw-text-primary-grey">
                          {question.label}
                        </div>
                        <div className="tw-text-sm tw-font-normal tw-leading-5 tw-text-dark-black">
                          {response || 'N/A'}
                        </div>
                      </div>
                    );
                  })}
                  <PlusOneSessionsSection plusOneId={plusOneGuest.plusOneId} />
                  <div className="tw-flex tw-gap-3">
                    <Button
                      variant="link"
                      className="tw-h-auto tw-w-fit tw-p-0 tw-text-sm tw-font-medium"
                      onClick={() => setSelectedPlusOneId(plusOneGuest.plusOneId)}
                    >
                      Go to guest <ArrowRight size={16} className="tw-ml-1" />
                    </Button>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </div>
      <PlusOneGuestModal
        open={isGuestModalOpen}
        onOpenChange={setIsGuestModalOpen}
        onGuestAdded={handleGuestAdded}
      />
    </>
  );
};
